<template>
  <div
    class="scroll-wrapper"
    :id="randomId">
    <div>
      <div v-if="totalList?.length">
        <div class="pulldown-wrapper">
          <div v-if="finishedDown">
            <span v-if="!instance?.slots?.finishPullDown">已经是第一页了</span>
            <slot name="finishPullDown"></slot>
          </div>
          <div v-else>
            <div v-if="isPullDown">
              <span v-if="!instance?.slots?.pullDowning">加载中...</span>
              <slot name="pullDowning"></slot>
            </div>
            <div v-else>
              <span v-if="!instance?.slots?.finishedPullDown">加载完成</span>
              <slot name="finishedPullDown"></slot>
            </div>
          </div>
        </div>
        <template v-for="page in totalList">
          <div v-for="(row, rowIndex) in page.data">
            <div>
              <slot
                :row="row"
                :current="page.current"
                :rowIndex="rowIndex"></slot>
            </div>
          </div>
        </template>
        <div class="pullup-wrapper">
          <div v-if="finishedUp">
            <span v-if="!instance?.slots?.finishPullUp">已经到底了</span>
            <slot
              name="finishPullUp"
              v-else></slot>
          </div>
          <div v-else>
            <div v-if="isPullUp">
              <span v-if="!instance?.slots?.pullUping">加载中...</span>
              <slot
                name="pullUping"
                v-else></slot>
            </div>
            <div v-else>
              <span v-if="!instance?.slots?.finishedPullUp">加载完成</span>
              <slot
                name="finishedPullUp"
                v-else></slot>
            </div>
          </div>
        </div>
      </div>
      <div
        class="scroll-nodata"
        v-else>
        <iconfont
          name="icon-rongqi-6"
          class="text-[150px] mb-sm"></iconfont>
        <div>暂无数据</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, getCurrentInstance, PropType, watch } from "vue";
import BetterScroll from "@better-scroll/core";
import Pullup from "@better-scroll/pull-up";
import PullDown from "@better-scroll/pull-down";
import ObserveDOM from "@better-scroll/observe-dom";
import { onBeforeUnmount } from "vue";

BetterScroll.use(Pullup);
BetterScroll.use(PullDown);
BetterScroll.use(ObserveDOM);

const instance = getCurrentInstance();
/**
 * @function generateRandomLetter 生成一段随机的英文字符
 * @param {number} length 随机字符长度
 */
const generateRandomLetter = (length: number): string => {
  const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
  let result = "";

  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * charset.length);
    result += charset[randomIndex];
  }

  return result;
};

const randomId = generateRandomLetter(20); //随机id名称，避免同一页面实用组件id重复
const props = defineProps({
  // 列表数组数据源，必传
  data: {
    type: Object as PropType<any>
  },
  // 总条数数据，必传
  total: {
    type: Number,
    default: 0
  },
  // 一页的大小数据，必传
  pageSize: {
    type: Number,
    default: 10
  },
  // 当前页面的页码，必传
  pageNum: {
    type: Number,
    default: 1
  }
});

const emits = defineEmits(["next", "prev"]); //next: 上一页  prev:下一页
const isPullUp = ref(true); //是否允许上一页翻页
const finishedUp = ref(false); //上一页翻页是否完成
const isPullDown = ref(true); //是否允许下一页翻页
const finishedDown = ref(false); //下一页翻页是否完成
const bscroll = ref(); //betterscroll的ref对象
const totalList = ref<any>([]); //总数据源

onMounted(() => {
  bscroll.value = new BetterScroll(document.querySelector("#" + randomId) as any, {
    mouseWheel: true,
    probeType: 3,
    click: true,
    pullUpLoad: true,
    pullDownRefresh: true,
    observeDOM: true,
    bounceTime: 600
  });
  // 下滑翻页状态管理
  bscroll.value.on("pullingUp", () => {
    console.log(props.pageNum, "next");

    const maxPage = Math.ceil(props.total / props.pageSize);
    if (totalList.value?.length && totalList.value[totalList.value?.length - 1].current < maxPage) {
      isPullUp.value = true;
      emits("next", totalList.value[totalList.value?.length - 1].current);
    } else {
      emits("next", false);
      isPullUp.value = false;
      finishedUp.value = true;
      bscroll.value.finishPullUp();
    }
  });
  // 上拉翻页状态管理
  bscroll.value.on("pullingDown", () => {
    console.log(props.pageNum, "prev");
    isPullDown.value = true;
    const maxPage = Math.ceil(props.total / props.pageSize);
    if (totalList.value?.length && totalList.value[0].current < maxPage && props.pageNum > 1) {
      emits("prev", totalList.value[0].current);
    } else {
      emits("prev", false);
      isPullDown.value = false;
      finishedDown.value = true;
      bscroll.value.finishPullDown();
    }
  });
});

/**
 * @function reset 重置所有数据
 * */
const reset = () => {
  totalList.value = [];
  isPullUp.value = true;
  finishedUp.value = false;
  isPullDown.value = true;
  finishedDown.value = false;
};

const watchClose = watch(
  () => props.data,
  (v) => {
    // 根据传入的数据判断是否该页还有数据，有数据的情况根据props.pageNum判断页码，放入数据
    if (v && v?.length) {
      if (totalList.value?.length) {
        const firstElPage = totalList.value[0].current;
        const lastElPage = totalList.value[totalList.value.length - 1].current;
        if (props.pageNum) {
          if ((props.pageNum > lastElPage || firstElPage == lastElPage) && firstElPage <= props.pageNum) {
            totalList.value.push({
              current: props.pageNum,
              data: v
            });
            setTimeout(() => {
              isPullUp.value = false;
              bscroll.value && bscroll.value.finishPullUp();
              setTimeout(() => {
                isPullUp.value = true;
              }, 1000);
            }, 500);
          } else {
            totalList.value.unshift({
              current: props.pageNum,
              data: v
            });
            setTimeout(() => {
              isPullDown.value = false;
              bscroll.value && bscroll.value.finishPullDown();
              setTimeout(() => {
                isPullDown.value = true;
              }, 1000);
            }, 500);
          }
        } else {
          console.error("请传入页码");
        }
      } else {
        totalList.value.push({
          current: props.pageNum,
          data: v
        });
      }

      const maxPage = Math.ceil(props.total / props.pageSize);
      if (props.pageNum >= maxPage) {
        isPullUp.value = false;
        finishedUp.value = true;
        bscroll.value && bscroll.value.finishPullUp();
      }
      if (props.pageNum <= 1) {
        isPullDown.value = false;
        finishedDown.value = true;
        bscroll.value && bscroll.value.finishPullDown();
      }
    }

    // console.log(totalList.value, "totalList");
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  watchClose && watchClose();
  reset();
});

defineExpose({
  reset
});
</script>

<style lang="less" scoped>
.scroll-nodata {
  padding: 80px 0;
  font-size: 12px;
  color: #86909c;
  text-align: center;
}
.scroll-wrapper {
  overflow: hidden;
  position: relative;
}
.pulldown-wrapper {
  width: 100%;
  text-align: center;
  transform: translateY(-100%);
  font-size: 12px;
  color: #86909c;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  padding: 8px 0;
}
.pullup-wrapper {
  height: 80px;
  text-align: center;
  color: #86909c;
  font-size: 12px;
  padding-bottom: 8px;
}
</style>
